import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Menu, useIsInBottomSheet, useOverlayContext } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ServiceBuilder from '~/components/ServiceBuilder/ServiceBuilder';
import { defaultBillingDayOfWeek, recurrencePeriodsTextsPath } from '~/constants';
import { useActiveServiceTemplates } from '~/hooks/reactQuery/queries/useActiveServiceTemplates';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { BillingDayOfMonthModel, serviceCostModel, serviceModel, serviceTemplateModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useStoreStatic } from '~/store/store-context';
import { StartBillingOnEnum } from '~/types/api.model';
import { NewServiceType, SelectNewServiceTypeModal } from '../SelectNewServiceTypeModal/SelectNewServiceTypeModal';
export function serviceFromServiceTemplate(param) {
    let { serviceTemplate, agreementData, businessSettings } = param;
    const skipBillingOnAcceptance = agreementData.isActiveAgreement() || ((businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billUpfront) ? businessSettings.startBillingOn === StartBillingOnEnum.SkipBillingOnAcceptance : true);
    return serviceModel.fromServiceTemplate(serviceTemplate, agreementData.serviceSource(), {
        isBilledUpfront: (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billUpfront) || false,
        skipBillingOnAcceptance,
        billingDayOfWeek: (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfWeek) || defaultBillingDayOfWeek,
        billingDayOfMonth: BillingDayOfMonthModel.make(businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfMonth),
        prorateFirstInvoice: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.prorateFirstInvoice,
        isPaused: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.automaticServicesPausedByDefault
    });
}
export function formatServiceSubtext(s, recurrencePeriodsTexts) {
    const isBundle = serviceTemplateModel.isBundle(s);
    const subtextFirstPart = isBundle ? serviceTemplateModel.formatNumIncludedServices(s) : serviceCostModel.stringifyServiceCost(s.cost).costDisplay;
    const subtextSecondPart = serviceTemplateModel.formatExtendedRecurrencePeriod(s, false, recurrencePeriodsTexts);
    return "".concat(subtextFirstPart, " • ").concat(subtextSecondPart);
}
export const GetAddServicesPopoverTID = (parentTestId)=>getTestIds(parentTestId);
export function AddServicesPopover(param) {
    let { testId, agreementData, onSelectServiceTemplate, onClose } = param;
    const { t: texts } = useLocaleKeys('components.addServicesButton');
    const { t: popoverTexts } = useLocaleKeys('components.addServicesButton.popover');
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const { AddServicesPopoverTID } = useMemo(()=>GetAddServicesPopoverTID(testId), [
        testId
    ]);
    const { getState } = useStoreStatic();
    const { data: serviceTemplates, isLoading, refresh } = useActiveServiceTemplates({
        refreshOnMount: true
    });
    const serviceTemplateApi = useServiceTemplateApi();
    const { showModal, startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const isInBottomSheet = useIsInBottomSheet();
    function selectServiceTemplate(serviceTemplate) {
        const service = serviceFromServiceTemplate({
            serviceTemplate,
            agreementData,
            businessSettings: selectors.user.businessSettings(getState())
        });
        onSelectServiceTemplate(service);
    }
    async function createNewServiceTemplate() {
        const newServiceType = await showModal({
            biName: 'select_new_service_type',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            content: (p)=>/*#__PURE__*/ _jsx(SelectNewServiceTypeModal, _object_spread({}, p))
        });
        if (!newServiceType) return;
        if (isInBottomSheet) {
            onClose();
        }
        showModal({
            biName: 'service_builder',
            closeOnEscapeKey: true,
            content: (p)=>/*#__PURE__*/ _jsx(ServiceBuilder, _object_spread({
                    serviceNameBlacklist: serviceTemplates ? serviceTemplates.map((s)=>s.name) : [],
                    agreementData: agreementData,
                    serviceOrigin: agreementData.isActiveAgreement() ? 'agreement' : 'draft',
                    isCreatingBundle: newServiceType === NewServiceType.Bundle,
                    onSave: async (output)=>{
                        const body = serviceTemplateModel.fromService(output);
                        const loaderName = 'createNewServiceLoader';
                        startLoader(loaderName);
                        const result = await handlePromise(serviceTemplateApi.create(body));
                        if (result.success) {
                            await refresh({
                                throwOnError: false
                            });
                            const newServiceTemplate = serviceTemplateModel.setId(body, result.data.id);
                            selectServiceTemplate(newServiceTemplate);
                        } else {
                            showErrorToast(texts.failedToCreateService(), AddServicesPopoverTID.ErrorToast());
                        }
                        stopLoader(loaderName);
                        return result;
                    }
                }, p))
        });
    }
    return /*#__PURE__*/ _jsx(Menu, {
        dimensions: {
            height: 344
        },
        loading: isLoading,
        emptyText: texts.noMatchesFound(),
        searchProps: {
            showSearch: true,
            autoFocus: true,
            bi: {
                name: 'service_search'
            },
            strategy: {
                type: 'text'
            }
        },
        testId: AddServicesPopoverTID.Menu(),
        items: (serviceTemplates || []).map((s)=>({
                id: s.id,
                title: s.name,
                body: formatServiceSubtext(s, recurrencePeriodsTexts),
                badge: serviceTemplateModel.isBundle(s) ? {
                    text: popoverTexts.bundle()
                } : undefined,
                bi: {
                    name: 'service_template',
                    data: {
                        service_template_id: s.id
                    }
                },
                onClick: ()=>selectServiceTemplate(s)
            })),
        footer: {
            items: [
                {
                    variant: 'contextualAction',
                    props: {
                        title: popoverTexts.createNew(),
                        intent: Intent.CreateServiceTemplate,
                        icon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                        testId: AddServicesPopoverTID.CreateNew(),
                        onClick: createNewServiceTemplate,
                        bi: {
                            name: 'create_new'
                        }
                    }
                }
            ]
        }
    });
}
